<template>
	<aside class="db2-settings">
		<div class="db2-settings-title">Settings</div>
		<HaqTextField :value="draft.name" label="Title" hide-details @input="$emit('patch', { name: $event })" />
		<HaqTextarea class="db2-settings-desc" :value="draft.description" label="Description" hide-details rows="4" @input="$emit('patch', { description: $event })" />
		<HaqSelect
			:value="draft.siteId"
			:items="siteOptions"
			item-text="name"
			item-value="objectId"
			label="Default site"
			clearable
			hide-details
			@input="$emit('patch', { siteId: $event, site: $event || null })"
		/>
		<HaqSelect
			:value="draft.defaultTimeRange"
			:items="ranges"
			item-text="name"
			item-value="value"
			label="Default time range"
			hide-details
			@input="$emit('patch', { defaultTimeRange: $event })"
		/>
		<HaqSelect
			:value="headerArt"
			:items="headerArtOptions"
			item-text="text"
			item-value="value"
			label="Header image"
			hide-details
			@input="$emit('patch', { headerArt: $event })"
		/>
		<HaqAutocomplete
			:value="draft.icon"
			:items="iconItems"
			item-text="text"
			item-value="value"
			label="Icon"
			hide-details
			:disabled="headerArt === 'sitePhoto'"
			:filter="iconFilter"
			:menu-props="iconMenuProps"
			@input="$emit('patch', { icon: $event })"
		>
			<template v-slot:item="{ item }">
				<span class="db2-icon-sel">
					<v-icon class="mr-2" small>{{ iconGlyph(item) }}</v-icon>
					<span>{{ iconLabel(item) }}</span>
				</span>
			</template>
			<template v-slot:selection="{ item }">
				<span class="db2-icon-sel">
					<v-icon class="mr-2" small>{{ iconGlyph(item) }}</v-icon>
					<span>{{ iconLabel(item) }}</span>
				</span>
			</template>
		</HaqAutocomplete>
	</aside>
</template>

<script>
import HaqTextField from '@/components/form/HaqTextField.vue';
import HaqTextarea from '@/components/form/HaqTextarea.vue';
import HaqSelect from '@/components/form/HaqSelect.vue';
import HaqAutocomplete from '@/components/form/HaqAutocomplete.vue';
import { TIME_RANGES, DASHBOARD_ICONS, DASHBOARD_ICON_MENU_PROPS, HEADER_ART_OPTIONS, dashboardHeaderArt, filterDashboardIcon } from './dashboardModel';

/**
 * Edit-mode dashboard settings rail.
 */
export default {
	name: 'DashboardSettings',
	components: { HaqTextField, HaqTextarea, HaqSelect, HaqAutocomplete },
	props: {
		draft: { type: Object, required: true },
		sites: { type: Array, default: () => [] }
	},
	data() {
		return {
			ranges: TIME_RANGES,
			icons: DASHBOARD_ICONS,
			iconMenuProps: DASHBOARD_ICON_MENU_PROPS,
			headerArtOptions: HEADER_ART_OPTIONS
		};
	},
	computed: {
		/**
		 * Site dropdown items.
		 * @returns {Array}
		 */
		siteOptions() {
			return this.sites || [];
		},
		/**
		 * Icon vs site photo for the dashboard header.
		 * @returns {'icon'|'sitePhoto'}
		 */
		headerArt() {
			return dashboardHeaderArt(this.draft);
		},
		/**
		 * Icon select items with a readable name and mdi value.
		 * @returns {Array<{ text: string, value: string }>}
		 */
		iconItems() {
			const list = (this.icons || []).slice();
			const cur = this.draft && this.draft.icon;
			if (cur && list.indexOf(cur) < 0) list.unshift(cur);
			return list.map(icon => ({
				text: String(icon).replace(/^mdi-/, '').replace(/-/g, ' '),
				value: icon
			}));
		}
	},
	methods: {
		/**
		 * Icon glyph from a select item (object or string).
		 * @param {string|{ value?: string }} item
		 * @returns {string}
		 */
		iconGlyph(item) {
			if (item && typeof item === 'object') return item.value || '';
			return item || '';
		},
		/**
		 * Readable icon name from a select item (object or string).
		 * @param {string|{ text?: string, value?: string }} item
		 * @returns {string}
		 */
		iconLabel(item) {
			if (item && typeof item === 'object') {
				return item.text || String(item.value || '').replace(/^mdi-/, '').replace(/-/g, ' ');
			}
			return String(item || '').replace(/^mdi-/, '').replace(/-/g, ' ');
		},
		/**
		 * Show the full icon list when the menu opens with the current label as search.
		 * @param {string|{ value?: string }} item
		 * @param {string} queryText
		 * @param {string} itemText
		 * @returns {boolean}
		 */
		iconFilter(item, queryText, itemText) {
			const cur = this.draft && this.draft.icon;
			const selected = String(cur || '').replace(/^mdi-/, '').replace(/-/g, ' ');
			return filterDashboardIcon(item, queryText, itemText, selected);
		}
	}
};
</script>
